/**
 * Main entry point for Advisor Tools application
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { applyStoredTheme } from '@aaimplatform/ui/styles';
import App from './App';
import { AdvisorServiceMissingError } from './components/AdvisorServiceNotice';
import './index.css';

// Import institutional design system styles
import './styles/tailwind.css';

/**
 * Pre-paint theme bootstrap (GH #2305). Applies the operator's stored
 * `aaim-theme` preference -- or `prefers-color-scheme` when none is stored --
 * to `documentElement` SYNCHRONOUSLY, before createRoot renders and before the
 * browser paints its first frame. This is the flash-of-wrong-theme guard: the
 * `dark` class must already be on the root when the first paint resolves the
 * `--color-*` custom properties. The ThemeToggle in the header only reconciles
 * its own glyph after mount.
 */
applyStoredTheme();

// Create React Query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      // One retry, except when the advisor data service is not deployed: a retry
      // only delays the not-available notice.
      retry: (failureCount, error) => !(error instanceof AdvisorServiceMissingError) && failureCount < 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);
